import { match } from 'ts-pattern'
import useDailyClaim from 'src/services/helpers/dailyClaim'
import type { QuestV2 } from './types'

type DerivedStatus = QuestV2['status']

/**
 * Everything {@link QuestCardV2} needs to render itself. Default values come
 * straight from QuestV2 progress fields (`quest.rewards`, `quest.status`, the
 * localStorage-backed `useQuestCardDot`). Per-buttonAction branches in
 * {@link useQuestCardViewModel} can override any subset of fields for "fake
 * quests" whose source of truth lives outside the QuestV2 progress system —
 * e.g. `mainpage_daily_claim` reads from the standalone daily-claim API.
 *
 * To add a new override dimension: extend this interface and (when the field
 * has a useful baseline) default it in `buildDefaultViewModel`. Existing
 * button actions pick up the new default automatically. New button actions
 * spread `...base` and only spell out the fields they actually override.
 */
export interface QuestCardViewModel {
  /** Numeric reward shown beside the gem icon. */
  rewards: number
  /** Whether the quest is in a "claimed" state (line-through + disabled). */
  isRewarded: boolean
  /** Status driving the `questCardStyles({ status })` tv variant. */
  derivedStatus: DerivedStatus
  /** Whether the small red notification dot should render. */
  showDot: boolean
  /**
   * Extra className appended to the card root, for per-action styling that
   * cannot be expressed by the `status` variant alone (custom borders,
   * highlights, gradients, etc.). Undefined leaves the card on the existing
   * tv-driven styles.
   */
  cardClassName?: string
  /** Extra className appended to the action button. */
  buttonClassName?: string
}

/** Inputs the view model needs from hooks the parent already owns. */
export interface QuestCardViewModelDefaults {
  /** `useQuestCardDot(quest).showDot` — localStorage-driven baseline. */
  baseShowDot: boolean
}

function buildDefaultViewModel(
  quest: QuestV2,
  defaults: QuestCardViewModelDefaults,
): QuestCardViewModel {
  return {
    rewards: quest.rewards ? Number(quest.rewards) : 0,
    isRewarded: quest.status === 'rewarded',
    derivedStatus: quest.status,
    showDot: defaults.baseShowDot,
  }
}

/**
 * Compute the full QuestCard view model.
 *
 * Hooks (`useDailyClaim` and any future external sources) are called
 * unconditionally to satisfy the rules of hooks; their values are only
 * consumed by the matching `match(...).with(...)` branch.
 */
export function useQuestCardViewModel(
  quest: QuestV2,
  defaults: QuestCardViewModelDefaults,
): QuestCardViewModel {
  const dailyClaim = useDailyClaim()
  const base = buildDefaultViewModel(quest, defaults)

  return match(quest.buttonAction)
    .returnType<QuestCardViewModel>()
    .with('mainpage_daily_claim', () => ({
      ...base,
      rewards: dailyClaim.amount,
      isRewarded: !dailyClaim.available,
      derivedStatus: dailyClaim.available ? 'unclaimed' : 'rewarded',
      showDot: dailyClaim.available,
    }))
    .otherwise(() => base)
}
